import React from "react";
import { motion } from "framer-motion";
import { X, Check } from "lucide-react";

const problems = [
  "Credits get lost across releases.",
  "Great people stay invisible.",
  "Industry relationships are fragmented.",
  "Talent is hard to verify.",
  "Reputation lives in screenshots, DMs, word of mouth.",
  "Career stories disappear across platforms.",
  "New talent struggles to access the right rooms.",
];

const solutions = [
  "Verified collaborations on every project.",
  "Professionally represented identity.",
  "Trusted reviews from people you've worked with.",
  "Network intelligence across studios and tours.",
  "Career timeline that grows with you.",
  "Industry mapping of teams, labels and crews.",
  "Reputation visibility that opens doors.",
];

export const IndustryProblem = () => {
  return (
    <section
      id="industry"
      data-testid="industry-problem-section"
      className="relative bg-loudar-ink py-24 md:py-32"
    >
      <div className="container-loudar">
        <div className="mb-14 md:mb-20">
          <span className="label-mono">01 — The industry truth</span>
          <h2
            data-testid="industry-headline"
            className="mt-4 font-display text-4xl uppercase leading-[0.95] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl"
          >
            The Music Industry Runs On Reputation.
          </h2>
          <p className="mt-5 max-w-2xl text-base text-white/60 md:text-lg">
            But there's never been a professional system built to organise it.
            Until now.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-px overflow-hidden rounded-sm border border-white/[0.08] md:grid-cols-2">
          {/* Problems */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="bg-loudar-surface p-8 md:p-12"
            data-testid="industry-problems-col"
          >
            <div className="mb-8 flex items-center gap-3">
              <span className="grid h-8 w-8 place-items-center rounded-full border border-white/15 text-white/60">
                <X size={14} />
              </span>
              <span className="label-chrome">Today</span>
            </div>
            <ul className="space-y-5">
              {problems.map((p, i) => (
                <li
                  key={i}
                  className="border-b border-white/[0.06] pb-4 text-lg leading-snug text-white/55 last:border-none last:pb-0"
                >
                  <span className="mr-3 font-mono text-xs text-white/30">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Solutions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative bg-loudar-elevated p-8 md:p-12"
            data-testid="industry-solutions-col"
          >
            <div className="absolute right-0 top-0 h-1 w-24 bg-loudar-orange" />
            <div className="mb-8 flex items-center gap-3">
              <span className="grid h-8 w-8 place-items-center rounded-full border border-loudar-orange/60 bg-loudar-orange/10 text-loudar-orange">
                <Check size={14} />
              </span>
              <span className="label-mono">With Loudar</span>
            </div>
            <ul className="space-y-5">
              {solutions.map((s, i) => (
                <li
                  key={i}
                  className="border-b border-white/[0.06] pb-4 text-lg leading-snug text-white last:border-none last:pb-0"
                >
                  <span className="mr-3 font-mono text-xs text-loudar-orange">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {s}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default IndustryProblem;
